import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SHEET_EASE, prefersReducedMotion } from './tokens';
import { useSoft } from './SurfaceStyleContext';
import usePageScrollLock from '../../utils/usePageScrollLock';

/**
 * Mobile bottom sheet. Scrim fades 160ms, the sheet slides up 200ms on the handoff curve;
 * both are skipped under prefers-reduced-motion. Caps at 86% of the viewport and scrolls
 * inside. Sits above the sticky CTA bar and the global tab bar (z 60).
 *
 * The sheet is laid out in the VISUAL viewport, not the layout one: on a phone the keyboard
 * doesn't shrink `position: fixed; bottom: 0`, it covers it — so a sheet with a field in it
 * lost its lower half (results, the CTA) under the keys. The box follows
 * `window.visualViewport`'s height and offset instead, and eases as the keyboard comes and goes.
 */
/** `action`: an optional control drawn in the header beside ✕ (the event sheet's SHARE pill). */
export default function BottomSheet({ open, onClose, title, note, action = null, children, maxHeight = '86%', testId = 'bottom-sheet', header = true, zIndex = 60 }) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const [viewport, setViewport] = useState(null); // { top, height } of the visual viewport
  const closeT = useRef(null);
  const sheetRef = useRef(null);
  const reduced = prefersReducedMotion();
  const soft = useSoft();

  useEffect(() => {
    if (!mounted) return undefined;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return undefined;
    const sync = () => setViewport({ top: Math.round(vv.offsetTop), height: Math.round(vv.height) });
    sync();
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    return () => { vv.removeEventListener('resize', sync); vv.removeEventListener('scroll', sync); setViewport(null); };
  }, [mounted]);

  useEffect(() => {
    if (open) {
      window.clearTimeout(closeT.current);
      setMounted(true);
      // Next frame so the initial transform is painted before it animates. A timer backs
      // the frame up: rAF is throttled in background tabs and some embedded frames, and a
      // sheet that never slides in is worse than one that skips the animation.
      // Reading the sheet's box first commits its off-screen transform as the transition's
      // start value; without that read the two styles can collapse into one and the sheet
      // simply appears where it should have slid to.
      const show = () => { void sheetRef.current?.offsetHeight; setShown(true); };
      const id = window.requestAnimationFrame(show);
      const t = window.setTimeout(show, 40);
      return () => { window.cancelAnimationFrame(id); window.clearTimeout(t); };
    }
    setShown(false);
    closeT.current = window.setTimeout(() => setMounted(false), reduced ? 0 : 220);
    return () => window.clearTimeout(closeT.current);
  }, [open, reduced]);

  // The page behind is frozen by `usePageScrollLock` (body pinned at -scrollY), NOT by
  // body { overflow: hidden } — iOS scrolls <html>, so that lock does nothing there and the
  // page slid under the sheet on every tap, cancelling the click as a scroll gesture.
  usePageScrollLock(mounted);

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mounted, onClose]);

  if (!mounted || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className="pr-overlay-root fixed left-0 right-0 flex items-end"
      style={{
        zIndex, background: 'rgba(0,0,0,0.45)', opacity: shown ? 1 : 0,
        // The visual viewport's box while the keyboard is up; the whole screen otherwise.
        top: viewport ? viewport.top : 0,
        height: viewport ? viewport.height : undefined,
        bottom: viewport ? 'auto' : 0,
        transition: reduced ? 'none' : `opacity 160ms linear, height 160ms ${SHEET_EASE}, top 160ms ${SHEET_EASE}`,
      }}
      onClick={onClose}
      data-testid={`${testId}-scrim`}
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={`flex w-full flex-col bg-white pb-safe-bot ${soft ? '' : 'border-t-2 border-black'}`}
        style={{ maxHeight, borderRadius: soft ? '24px 24px 0 0' : 0, transform: shown ? 'translateY(0)' : 'translateY(100%)', transition: reduced ? 'none' : `transform 200ms ${SHEET_EASE}` }}
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        {header && (
          <div className={`flex flex-none items-center justify-between gap-3 ${soft ? '' : 'border-b-2 border-black'}`} style={{ padding: soft ? '16px 18px 8px' : '12px 14px' }}>
            <div className="min-w-0">
              <div className="truncate font-mono font-bold uppercase" style={{ fontSize: 12, letterSpacing: soft ? '0.16em' : '0.1em' }}>{title}</div>
              {note && <div className="font-mono font-bold uppercase" style={{ fontSize: soft ? 9 : 10, letterSpacing: soft ? '0.14em' : '0.1em', color: '#9CA3AF', marginTop: 2 }}>{note}</div>}
            </div>
            <div className="flex flex-none items-center gap-2">
              {action}
              <button type="button" onClick={onClose} aria-label="Close" className={`flex-none font-mono font-bold transition-colors duration-200 hover:bg-black hover:text-white ${soft ? 'rounded-full' : 'border-2 border-black bg-white'}`} style={{ width: 38, height: 38, fontSize: 15, background: soft ? '#F2F2F2' : undefined }}>✕</button>
            </div>
          </div>
        )}
        <div className="min-h-0 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
